import { PeriodType } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	resolveMoneyline,
	resolveOvertimeGeneric,
	resolveSpread,
	resolveTeamTotal,
	resolveTotalPoints,
	resolveGamePlayerProp,
} from '../../utils';

import {
	buildSpreadQuestion,
	buildTeamTotalQuestion,
	buildTotalGoalsQuestion,
	getPeriodScores,
} from '../../utils/market/hockey.utils';

export const hockeyConfig: Record<string, MarketConfig> = {
	// ── Game-Level Markets ──────────────────────────────────────────────

	Moneyline: {
		buildQuestion: (m) => `Will ${m.selection} win the game?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Game),
	},

	'Puck Line': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Game),
		isFinalRequired: true,
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Game),
	},

	'Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Game),
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Game),
	},

	'Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Game),
		hasProgressBar: true,
		isFinalRequired: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Game),
	},

	'Will There Be Overtime': {
		buildQuestion: () => `Will the game go to overtime?`,
		isFinalRequired: true,
		resolve: (_q, gc) => resolveOvertimeGeneric(gc, PeriodType.Period3, PeriodType.Period4),
	},

	// ── 1st Period ──────────────────────────────────────────────────────

	'1st Period Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 1st period?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period1),
	},

	'1st Period Puck Line': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period1),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period1),
	},

	'1st Period Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period1),
	},

	'1st Period Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period1),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period1),
	},

	// ── 2nd Period ──────────────────────────────────────────────────────

	'2nd Period Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 2nd period?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period2),
	},

	'2nd Period Puck Line': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period2),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period2),
	},

	'2nd Period Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period2),
	},

	'2nd Period Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period2),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period2),
	},

	// ── 3rd Period ──────────────────────────────────────────────────────

	'3rd Period Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 3rd period?`,
		resolve: (q, gc) => resolveMoneyline(getPeriodScores, q, gc, PeriodType.Period3),
	},

	'3rd Period Puck Line': {
		buildQuestion: (m) => buildSpreadQuestion(m.selection, m.points, PeriodType.Period3),
		resolve: (q, gc) => resolveSpread(getPeriodScores, q, gc, PeriodType.Period3),
	},

	'3rd Period Total Goals': {
		buildQuestion: (m) => buildTotalGoalsQuestion(m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTotalPoints(getPeriodScores, q, gc, PeriodType.Period3),
	},

	'3rd Period Team Total': {
		buildQuestion: (m) => buildTeamTotalQuestion(m.selection, m.points, PeriodType.Period3),
		hasProgressBar: true,
		resolve: (q, gc) => resolveTeamTotal(getPeriodScores, q, gc, PeriodType.Period3),
	},

	// ── Player Props ────────────────────────────────────────────────────

	'Player Goals': {
		buildQuestion: (m) => `Will ${m.selection} score at least ${m.points} goals?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'goals', ['score at least']),
	},

	'Player Assists': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} assists?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'assists', ['record at least']),
	},

	'Player Points': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} points?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'points', ['record at least']),
	},

	'Player Shots On Goal': {
		buildQuestion: (m) => `Will ${m.selection} record at least ${m.points} shots on goal?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'shots_on_goal', ['record at least']),
	},

	'Player Saves': {
		buildQuestion: (m) => `Will ${m.selection} make at least ${m.points} saves?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => resolveGamePlayerProp(q, gc, r, 'saves', ['make at least']),
	},
};
